/* eslint-disable jira/integration/test-id-by-folder-structure */
import React, { Fragment, memo, type ReactNode, useCallback } from 'react';
import { styled } from '@compiled/react';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { IconSkeleton } from '@atlassian/jira-horizontal-nav-icons/src/ui/icon/index.tsx';
import { useIntentionalHover } from '@atlassian/jira-onboarding/src/hooks/use-intentional-hover/use-intentional-hover.tsx';
import { EditableTitle } from './EditableTitle.tsx';

interface BaseProps {
	title?: string | null;
	withInlineEdit?: boolean;
	onConfirm?: (newTitle: string) => Promise<void>;
	onValidate?: (title: string, newTitle: string) => Promise<string | undefined>;
	id?: number;
	icon?: ReactNode | null;
	/** left-justified extra components, close to the title */
	extraComponentsClose?: ReactNode | ReactNode[] | null;
	/** right-justified extra components, far from the title */
	extraComponentsFar?: ReactNode | ReactNode[] | null;
	/** extra components, will be added to above of the title */
	extraComponentsAbove?: ReactNode | ReactNode[] | null;
	isLoading?: boolean;
	hasThemePicker?: boolean;
	testId?: string;
	fireAnalytics?: (action: string, actionSubject: string) => void;
}

export type WithInlineEditProps = {
	withInlineEdit: true;
	onConfirm: (newTitle: string) => void;
	onValidate: (title: string, newTitle: string) => Promise<string | undefined>;
	id: number;
};

type WithoutInlineEditProps = {
	withInlineEdit?: false;
	onConfirm?: never;
	onValidate?: never;
	id?: never;
};

type Props = (WithInlineEditProps | WithoutInlineEditProps) & BaseProps;

const BaseHeader = ({
	title,
	withInlineEdit,
	onConfirm,
	onValidate,
	id,
	icon,
	extraComponentsClose,
	extraComponentsFar,
	extraComponentsAbove,
	isLoading = false,
	testId,
	fireAnalytics,
}: Props) => {
	const WrapperElement = extraComponentsAbove ? Box : Fragment;

	const handleTitleIntentionalHover = useCallback(() => {
		if (fireAnalytics) {
			fireAnalytics('hovered', 'projectTitle');
		}
	}, [fireAnalytics]);

	const { onMouseEnter, onMouseLeave } = useIntentionalHover(() => {
		handleTitleIntentionalHover();
	}, 500);

	const handleTitleClick = useCallback(() => {
		if (fireAnalytics) {
			fireAnalytics('clicked', 'projectTitle');
		}
	}, [fireAnalytics]);

	if (isLoading) {
		return (
			<WrapperElement>
				{extraComponentsAbove && (
					<Inline space="space.100" xcss={extraComponentsAboveStyles}>
						<Box
							xcss={breadcrumbSkeletonStyles}
							testId="navigation-kit-header.extra-component-above-skeleton-1"
						/>
						<Box
							xcss={breadcrumbSkeletonStyles}
							testId="navigation-kit-header.extra-component-above-skeleton-2"
						/>
					</Inline>
				)}
				<Inline xcss={headerWrapperStyles}>
					<IconSkeleton data-testid="business-horizontal-nav.common.ui.header.icon-skeleton" />
					<Box
						xcss={titleSkeletonStyles}
						testId="business-horizontal-nav.common.ui.header.title-skeleton"
					/>
				</Inline>
			</WrapperElement>
		);
	}

	return (
		<WrapperElement>
			{extraComponentsAbove && (
				<Inline xcss={extraComponentsAboveStyles}>{extraComponentsAbove}</Inline>
			)}
			<Inline xcss={headerWrapperStyles} testId={testId}>
				{icon}

				{withInlineEdit ? (
					<EditableTitle key={id} title={title} onConfirm={onConfirm} onValidate={onValidate} />
				) : (
					// Analytics: Measuring user demand for project title editing feature
					// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
					<Box
						xcss={titleContainerStyles}
						onMouseEnter={onMouseEnter}
						onMouseLeave={onMouseLeave}
						onClick={handleTitleClick}
					>
						<Title>{title}</Title>
					</Box>
				)}

				{extraComponentsClose && <Inline alignInline="start">{extraComponentsClose}</Inline>}

				{extraComponentsFar && (
					<Inline alignInline="end" space="space.100" xcss={extraComponentsFarStyles}>
						{extraComponentsFar}
					</Inline>
				)}
			</Inline>
		</WrapperElement>
	);
};

export default memo<Props>(BaseHeader);
export type { Props };

const headerWrapperStyles = xcss({
	justifyContent: 'flex-start',
	gap: 'space.075',
	alignItems: 'center',
	minWidth: token('space.0'),
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
	height: token('space.400'),
});

const titleSkeletonStyles = xcss({
	boxSizing: 'border-box',
	height: token('space.300'),
	width: '200px',
	backgroundColor: 'color.skeleton',
	borderRadius: 'radius.small',
});

const breadcrumbSkeletonStyles = xcss({
	boxSizing: 'border-box',
	height: token('space.300'),
	width: '90px',
	backgroundColor: 'color.skeleton',
	borderRadius: 'radius.small',
});

const extraComponentsFarStyles = xcss({
	alignSelf: 'end',
	marginLeft: 'auto',
});

const extraComponentsAboveStyles = xcss({
	justifyContent: 'flex-start',
	alignItems: 'center',
	minWidth: token('space.0'),
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
});

const titleContainerStyles = xcss({
	display: 'grid',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Title = styled.h1({
	marginTop: 0, // Require to override inherited styles
	// Cannot easily use color token here - var(--project-color-text) is sometimes overridden black -> white
	// See jira/src/packages/business/navigation/horizontal-nav/integration-tests/accessibility.spec.tsx
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	color: Tokens.COLOR_TEXT,
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	font: token('font.heading.medium'),
});
